import { Editor, Mark, Node } from '@tiptap/core';
import Paragraph from '@tiptap/extension-paragraph';
import { AtomFormat, ParagraphFormat, RunStyle, splitFormats } from './richDoc';
import {
  choiceLabel,
  IInlineChoice,
  IMigrationMark,
  migrationLabel,
} from './richTextModel';

/**
 * Die Bausteine des Texteditors (#1245, Teil 48) — Tiptap-Erweiterungen für genau das Modell aus
 * `richTextModel`, nicht für freies HTML. Umgewandelt wird in `richDoc.ts`; hier steht nur, wie
 * es im Editor aussieht und sich verhält.
 */

const json = <T>(value: string | null, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const HEADING = new Set(['heading1', 'heading2', 'heading3']);

const INDENT_CSS: Record<string, string> = {
  single: 'margin-left: 1.25cm;',
  double: 'margin-left: 2.5cm;',
  hanging: 'padding-left: 1.25cm; text-indent: -1.25cm;',
};

/** Wie ein Absatz im Editor aussieht — dieselben Regeln wie in der Leseansicht. */
export const paragraphCss = (format: ParagraphFormat): string => {
  const parts: string[] = [];
  if (!format.style && format.alignment) {
    parts.push(
      `text-align: ${format.alignment === 'both' ? 'justify' : format.alignment};`
    );
  }
  if (format.style && HEADING.has(format.style)) {
    parts.push('font-weight: 700;');
    if (format.style === 'heading1') parts.push('font-size: 1.2rem;');
    if (format.style === 'heading2') parts.push('font-size: 1.1rem;');
  }
  if (!format.style && format.indent)
    parts.push(INDENT_CSS[format.indent] ?? '');
  if (!format.style && typeof format.spaceBefore === 'number') {
    parts.push(`margin-top: ${format.spaceBefore}pt;`);
  }
  if (!format.style && typeof format.spaceAfter === 'number') {
    parts.push(`margin-bottom: ${Math.max(format.spaceAfter, 2)}pt;`);
  }
  return parts.join(' ');
};

const runCss = (style: RunStyle): string =>
  [
    style.fontFamily ? `font-family: "${style.fontFamily}";` : '',
    typeof style.fontSize === 'number' ? `font-size: ${style.fontSize}pt;` : '',
    style.color ? `color: #${style.color};` : '',
    style.underlineStyle
      ? `text-decoration-style: ${style.underlineStyle};`
      : '',
  ]
    .filter(Boolean)
    .join(' ');

const atomCss = (format: AtomFormat): string =>
  [
    format.bold ? 'font-weight: 700;' : '',
    format.italic ? 'font-style: italic;' : '',
    format.underline ? 'text-decoration: underline;' : '',
    runCss(format),
  ]
    .filter(Boolean)
    .join(' ');

/**
 * »Eingabe« teilt den Absatz wie in Word (`splitFormats`): der erste Teil endet, der zweite
 * beginnt keine neue Seite, nach einer Überschrift folgt Fließtext.
 */
const splitParagraph = (editor: Editor): boolean => {
  const { state } = editor;
  const { $from, $to } = state.selection;
  if ($from.parent.type.name !== 'paragraph') {
    return false;
  }
  const format = ($from.parent.attrs.format ?? {}) as ParagraphFormat;
  const atEnd = $to.parentOffset === $to.parent.content.size;
  const { first, second } = splitFormats(format, atEnd);
  return editor
    .chain()
    .command(({ tr }) => {
      tr.deleteSelection();
      const position = tr.selection.$from.pos;
      tr.split(position, 1, [
        { type: state.schema.nodes.paragraph, attrs: { format: second } },
      ]);
      const firstStart = tr.doc.resolve(position).before();
      tr.setNodeMarkup(firstStart, undefined, { format: first });
      tr.scrollIntoView();
      return true;
    })
    .run();
};

export const DinoParagraph = Paragraph.extend({
  addAttributes() {
    return {
      format: {
        default: {},
        keepOnSplit: true,
        parseHTML: element => json(element.getAttribute('data-format'), {}),
        renderHTML: attributes => {
          const format = (attributes.format ?? {}) as ParagraphFormat;
          return {
            'data-format': JSON.stringify(format),
            'data-list': format.list ?? undefined,
            'data-page-break': format.pageBreakBefore ? 'true' : undefined,
            'data-continues': format.continues ? 'true' : undefined,
            style: paragraphCss(format),
          };
        },
      },
    };
  },
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => splitParagraph(editor as Editor),
    };
  },
});

/** Direktformatierung aus einem Klon (Schriftart, Größe, Farbe, Art der Unterstreichung). */
export const RunStyleMark = Mark.create({
  name: 'runStyle',
  addAttributes() {
    return {
      style: {
        default: {},
        parseHTML: element => json(element.getAttribute('data-run-style'), {}),
        renderHTML: attributes => ({
          'data-run-style': JSON.stringify(attributes.style ?? {}),
          style: runCss((attributes.style ?? {}) as RunStyle),
        }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-run-style]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },
});

/** Wie ein Datenfeld heißt — aus dem Feldkatalog, der erst später geladen sein kann. */
export interface LabelSource {
  current: (selector: string, label: string | null) => string;
}

const formatAttribute = {
  default: {},
  parseHTML: (element: HTMLElement) =>
    json(element.getAttribute('data-format'), {}),
  renderHTML: (attributes: Record<string, unknown>) => ({
    'data-format': JSON.stringify(attributes.format ?? {}),
    style: atomCss((attributes.format ?? {}) as AtomFormat),
  }),
};

/** Zählt nur hoch, damit der Editor die Beschriftungen neu zeichnet; wird nie gespeichert. */
const refreshAttribute = { default: 0, renderHTML: () => ({}) };

export const DataFieldNode = Node.create<{ labels: LabelSource }>({
  name: 'dataField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addOptions() {
    return { labels: { current: (selector, label) => label || selector } };
  },
  addAttributes() {
    return {
      selector: {
        default: '',
        parseHTML: element => element.getAttribute('data-selector') ?? '',
        renderHTML: attributes => ({ 'data-selector': attributes.selector }),
      },
      label: {
        default: null,
        parseHTML: element => element.getAttribute('data-label') || null,
        renderHTML: attributes => ({ 'data-label': attributes.label ?? '' }),
      },
      format: formatAttribute,
      refresh: refreshAttribute,
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-selector]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      { ...HTMLAttributes, class: 'dino-field', contenteditable: 'false' },
      this.options.labels.current(
        String(node.attrs.selector ?? ''),
        (node.attrs.label as string | null) ?? null
      ),
    ];
  },
  renderText({ node }) {
    return String(node.attrs.label || node.attrs.selector || '');
  },
});

export const InlineChoiceNode = Node.create({
  name: 'inlineChoice',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      choice: {
        default: { alternatives: [] },
        parseHTML: element =>
          json(element.getAttribute('data-choice'), { alternatives: [] }),
        renderHTML: attributes => ({
          'data-choice': JSON.stringify(attributes.choice ?? {}),
        }),
      },
      format: formatAttribute,
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-choice]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      { ...HTMLAttributes, class: 'dino-choice', contenteditable: 'false' },
      choiceLabel(node.attrs.choice as IInlineChoice),
    ];
  },
  renderText({ node }) {
    return choiceLabel(node.attrs.choice as IInlineChoice);
  },
});

export const OpenSpotNode = Node.create({
  name: 'openSpot',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      migration: {
        default: {},
        parseHTML: element => json(element.getAttribute('data-migration'), {}),
        renderHTML: attributes => ({
          'data-migration': JSON.stringify(attributes.migration ?? {}),
        }),
      },
      format: formatAttribute,
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-migration]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      { ...HTMLAttributes, class: 'dino-migration', contenteditable: 'false' },
      migrationLabel(node.attrs.migration as IMigrationMark),
    ];
  },
  renderText({ node }) {
    return `[${migrationLabel(node.attrs.migration as IMigrationMark)}]`;
  },
});
